import React from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { LogOut, User, Scale } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCompareIds } from "@/lib/compareStore";
import NotificationsBell from "@/components/site/NotificationsBell";
import { Button } from "@/components/ui/button";
import { promptLogin, promptRegister } from "@/lib/authRedirect";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Editorial pivot: The Reports (homepage) is the only primary nav item.
const nav = [
  { to: "/", label: "The Reports", testid: "nav-the-reports", match: (p) => p === "/" || p.startsWith("/jurnal") },
];

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const compareIds = useCompareIds();
  const { pathname } = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 bg-[#0F1F3D] text-white border-b border-white/10 shadow-[0_2px_20px_rgba(15,31,61,0.25)]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center group">
          {/* Mobile (<768px): shield + separator + wordmark, all-white on transparent
              bg → blends into the navy header. `brightness(0) invert(1)` is a
              no-op for white pixels but guards against any residual tint. */}
          <img
            src="/brand/tsr-mobile-header.png"
            alt="The School Reports"
            className="w-auto md:hidden group-hover:opacity-90 transition-opacity duration-200"
            style={{ height: "28px", filter: "brightness(0) invert(1)" }}
          />
          <img
            src="/brand/tsr-logotype-white.png"
            alt="The School Reports"
            className="w-auto hidden md:block group-hover:opacity-90 transition-opacity duration-200"
            style={{ height: "36px" }}
          />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end
              data-testid={n.testid}
              className={() =>
                `px-4 py-2 rounded-full text-sm transition-colors duration-200 ` +
                (n.match(pathname)
                  ? "bg-white/15 text-white font-semibold"
                  : "text-white/75 hover:text-white hover:bg-white/10")
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {compareIds.length > 0 && (
            <Link
              to="/compare"
              data-testid="compare-header-link"
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-white text-xs border border-white/20 transition-colors"
            >
              <Scale size={12} /> Bandingkan
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#E07A5F] text-white text-[10px] font-semibold">{compareIds.length}</span>
            </Link>
          )}
          <NotificationsBell />
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="w-10 h-10 rounded-full bg-white/15 border border-white/20 flex items-center justify-center text-white font-semibold hover:bg-white/25 transition-colors duration-200"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    user.name?.[0]?.toUpperCase() || <User size={16} />
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-medium">{user.name}</div>
                  <div className="text-xs text-muted-foreground">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                  <User size={14} className="mr-2" /> Profil saya
                </DropdownMenuItem>
                {(user.role === "admin" || user.role === "superadmin") && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    Dasbor editor
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                  <LogOut size={14} className="mr-2" /> Keluar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={() => promptLogin(navigate)}
                data-testid="header-login-btn"
                className="rounded-full text-white/85 hover:text-white hover:bg-white/10"
              >
                Masuk
              </Button>
              <Button
                onClick={() => promptRegister(navigate)}
                data-testid="header-register-btn"
                className="rounded-full bg-white text-[#0F1F3D] hover:bg-white/90"
              >
                Gabung Sekarang
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
